import React, { useContext, useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  People,
  Business,
  School,
  Assignment,
  HourglassEmpty,
  CheckCircle,
  Cancel,
  Groups,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { AuthContext } from '../../../contexts/AuthContext';
import { dashboardAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const { user, isSuperAdmin } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [enrollmentTrends, setEnrollmentTrends] = useState([]);

  useEffect(() => {
    fetchDashboardStats();
    fetchEnrollmentTrends();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getStats();
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollmentTrends = async () => {
    try {
      const response = await dashboardAPI.getEnrollmentTrends();
      setEnrollmentTrends(response.data.data);
    } catch (error) {
      console.error('Error fetching enrollment trends:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box>
        <Typography variant="h6" color="text.secondary">
          Unable to load dashboard statistics
        </Typography>
      </Box>
    );
  }

  // Build stats cards based on role
  const statsCards = [];

  // Student statistics (for both roles)
  statsCards.push(
    { title: 'Total Students', value: stats.students.total, icon: <People />, color: '#4F46E5' },
    { title: 'Pending Approval', value: stats.students.pending, icon: <HourglassEmpty />, color: '#F59E0B' },
    { title: 'Approved', value: stats.students.approved, icon: <CheckCircle />, color: '#10B981' },
    { title: 'Rejected', value: stats.students.rejected, icon: <Cancel />, color: '#EF4444' },
    { title: 'Alumni', value: stats.students.alumni, icon: <Groups />, color: '#8B5CF6' },
    { title: isSuperAdmin ? 'Total Courses' : 'My Branch Courses', value: stats.courses, icon: <School />, color: '#06B6D4' }
  );

  // Branch statistics (super admin only)
  if (isSuperAdmin && stats.branches) {
    statsCards.push(
      { title: 'Total Branches', value: stats.branches.total, icon: <Business />, color: '#10B981' },
      { title: 'Approved Branches', value: stats.branches.approved, icon: <CheckCircle />, color: '#10B981' },
      { title: 'Pending Branches', value: stats.branches.pending, icon: <HourglassEmpty />, color: '#F59E0B' },
      { title: 'Blocked Branches', value: stats.branches.blocked, icon: <Cancel />, color: '#EF4444' }
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Welcome back, {user?.name}!
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: stat.color,
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {React.cloneElement(stat.icon, { sx: { color: 'white', fontSize: 28 } })}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Student Status Distribution
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Approved', value: stats.students.approved, color: '#10B981' },
                    { name: 'Pending', value: stats.students.pending, color: '#F59E0B' },
                    { name: 'Rejected', value: stats.students.rejected, color: '#EF4444' },
                    { name: 'Alumni', value: stats.students.alumni, color: '#8B5CF6' },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ percent }) => percent > 0 ? `${(percent * 100).toFixed(0)}%` : ''}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[
                    { name: 'Approved', value: stats.students.approved, color: '#10B981' },
                    { name: 'Pending', value: stats.students.pending, color: '#F59E0B' },
                    { name: 'Rejected', value: stats.students.rejected, color: '#EF4444' },
                    { name: 'Alumni', value: stats.students.alumni, color: '#8B5CF6' },
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Monthly Enrollment Trends
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={enrollmentTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#4F46E5" strokeWidth={2} name="Enrollments" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
